import {
  ElementRef,
  Injector,
  QueryList,
  afterNextRender,
} from '@angular/core';

/** Any object that exposes a focus method (e.g. InputComponent). */
export interface Focusable {
  focus(options?: FocusOptions): void;
}

export type FocusTarget =
  | Focusable
  | HTMLElement
  | ElementRef<HTMLElement>
  | null
  | undefined;

export interface ScheduleFocusOptions {
  /** When the target is a host element, focus a descendant instead (e.g. `'button'`). */
  descendantSelector?: string;
  preventScroll?: boolean;
}

/** Whether a keyboard event is Enter or Space (button activation keys). */
export function isActivationKey(event: KeyboardEvent): boolean {
  return (
    event.key === 'Enter' ||
    event.key === ' ' ||
    event.key === 'Spacebar' ||
    event.code === 'Enter' ||
    event.code === 'NumpadEnter' ||
    event.code === 'Space'
  );
}

let keyboardModalityTrackingReady = false;
/** True after Tab/Enter/Space until the next pointer press. */
let keyboardModality = false;
let lastActivationKeyAt = 0;
let lastActivationButton: HTMLButtonElement | null = null;

function activationButtonFromEvent(event: Event): HTMLButtonElement | null {
  const button = (event.target as Element | null)?.closest?.('button');
  return button instanceof HTMLButtonElement ? button : null;
}

function initKeyboardModalityTracking(): void {
  if (keyboardModalityTrackingReady || typeof document === 'undefined') {
    return;
  }

  keyboardModalityTrackingReady = true;

  document.addEventListener(
    'keydown',
    (event) => {
      if (!(event instanceof KeyboardEvent)) {
        return;
      }

      if (event.key === 'Tab') {
        keyboardModality = true;
        return;
      }

      if (!isActivationKey(event)) {
        return;
      }

      keyboardModality = true;
      lastActivationKeyAt = Date.now();
      lastActivationButton = activationButtonFromEvent(event);
    },
    true,
  );

  document.addEventListener(
    'mousedown',
    (event) => {
      const button = activationButtonFromEvent(event);
      if (
        button &&
        button === lastActivationButton &&
        Date.now() - lastActivationKeyAt < 100
      ) {
        return;
      }

      keyboardModality = false;
      lastActivationButton = null;
    },
    true,
  );

  document.addEventListener(
    'pointerdown',
    (event) => {
      const button = activationButtonFromEvent(event);
      if (
        button &&
        button === lastActivationButton &&
        Date.now() - lastActivationKeyAt < 100
      ) {
        return;
      }

      keyboardModality = false;
      lastActivationButton = null;
    },
    true,
  );
}

if (typeof document !== 'undefined') {
  initKeyboardModalityTracking();
}

function buttonFromClickEvent(event: Event): HTMLButtonElement | null {
  const targetButton = (event.target as Element | null)?.closest?.('button');
  if (targetButton instanceof HTMLButtonElement) {
    return targetButton;
  }

  const hostButton = (event.currentTarget as Element | null)?.querySelector?.(
    'button',
  );
  return hostButton instanceof HTMLButtonElement ? hostButton : null;
}

/**
 * True when a button action should move focus after add/remove — i.e. the user
 * reached the button via keyboard (Tab) and activated with Enter/Space. Pointer
 * clicks focus the button but do not match `:focus-visible`.
 */
export function shouldScheduleFocusAfterAction(event?: Event): boolean {
  initKeyboardModalityTracking();

  if (!event) {
    return false;
  }

  const button = buttonFromClickEvent(event);
  if (!button) {
    return false;
  }

  const focusVisible = button.matches(':focus-visible');
  const keyboardFocused =
    keyboardModality && document.activeElement === button;
  const recentActivationKey =
    button === lastActivationButton &&
    Date.now() - lastActivationKeyAt < 500;
  const schedule = focusVisible || keyboardFocused || recentActivationKey;

  lastActivationButton = null;
  return schedule;
}

/**
 * Focuses a target after the next render. Use when DOM changes (add/remove rows,
 * toggles, etc.) would otherwise leave focus on a removed node or nowhere.
 */
export function scheduleFocusAfterRender(
  resolveTarget: () => FocusTarget,
  injector: Injector,
  options: ScheduleFocusOptions = {},
): void {
  afterNextRender(
    () => {
      focusTarget(resolveTarget(), options);
    },
    { injector },
  );
}

/** Focuses a target immediately. */
export function focusTarget(
  target: FocusTarget,
  options: ScheduleFocusOptions = {},
): void {
  const { descendantSelector, preventScroll = true } = options;
  const focusOptions: FocusOptions = { preventScroll };

  if (!target) {
    return;
  }

  if (isFocusable(target)) {
    target.focus(focusOptions);
    return;
  }

  const element = toHTMLElement(target);
  if (!element) {
    return;
  }

  const focusEl = descendantSelector
    ? element.querySelector<HTMLElement>(descendantSelector)
    : element;

  focusEl?.focus(focusOptions);
}

/**
 * Focuses the item at `index` once it exists in the QueryList and can accept
 * focus (e.g. after @empty → row transitions or mask inputs init).
 *
 * Prefers render signals over a fixed retry count:
 * - QueryList.changes when the new app-input is not in the list yet
 * - setTimeout(0) when the component exists but nested mask DOM is not ready
 */
export function scheduleFocusAtIndex<T extends FocusTarget>(
  items: QueryList<T>,
  index: number,
  injector: Injector,
  options: ScheduleFocusOptions = {},
): void {
  afterNextRender(
    () => {
      const tryFocus = (): boolean => {
        const target = items.get(index);
        if (!target) {
          return false;
        }
        focusTarget(target, options);
        return focusLandedOnField();
      };

      const focusWhenDomReady = () => {
        if (tryFocus()) {
          return;
        }
        // Phone mask may expose its inner <input> after this turn.
        setTimeout(() => tryFocus(), 0);
      };

      if (items.get(index)) {
        focusWhenDomReady();
        return;
      }

      const sub = items.changes.subscribe(() => {
        sub.unsubscribe();
        afterNextRender(focusWhenDomReady, { injector });
      });
    },
    { injector },
  );
}

/** Focuses the first item in a collection after the next render. */
export function scheduleFocusFirst<T extends FocusTarget>(
  items: QueryList<T>,
  injector: Injector,
  options: ScheduleFocusOptions = {},
): void {
  scheduleFocusAfterRender(() => items.first, injector, options);
}

/** Focuses the last item in a collection after the next render. */
export function scheduleFocusLast<T extends FocusTarget>(
  items: QueryList<T>,
  injector: Injector,
  options: ScheduleFocusOptions = {},
): void {
  scheduleFocusAfterRender(() => items.last, injector, options);
}

function focusLandedOnField(): boolean {
  const active = document.activeElement;
  return (
    active instanceof HTMLInputElement ||
    active instanceof HTMLTextAreaElement ||
    active instanceof HTMLSelectElement
  );
}

function isFocusable(target: FocusTarget): target is Focusable {
  return (
    target != null &&
    typeof target === 'object' &&
    'focus' in target &&
    typeof (target as Focusable).focus === 'function' &&
    !(target instanceof HTMLElement) &&
    !(target instanceof ElementRef)
  );
}

function toHTMLElement(target: FocusTarget): HTMLElement | null {
  if (!target || isFocusable(target)) {
    return null;
  }

  if (target instanceof HTMLElement) {
    return target;
  }

  if (target instanceof ElementRef) {
    return target.nativeElement;
  }

  return null;
}
